import { Feature, FeatureCollection } from "geojson";
import maplibregl, { LngLatBoundsLike, Map, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useEffect, useRef, useState } from "react";
import { RouteItemData } from "../../types";

interface RouteDetails extends RouteItemData {
  color: string;
}

interface Events {
  eventType: string;
  coords: { lat: number; lng: number };
  distance: number;
  date: string;
}

interface RouteMapProps {
  routes: RouteDetails[];
  events?: Events[];
  onEventClick?: (groupKey: string) => void;
}

const RouteMap: React.FC<RouteMapProps> = ({
  routes,
  events,
  onEventClick,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [mapKey, setMapKey] = useState(Date.now()); // Force re-render with key change

  // Function to initialize or reinitialize the map
  const initializeMap = () => {
    // Clean up previous map instance if it exists
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    // Make sure we have a container to render to
    if (!mapContainerRef.current) return;

    console.log("Initializing map...");

    // Create new map instance
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          "osm-tiles": {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            maxzoom: 19,
          },
        },
        layers: [
          {
            id: "osm-tiles",
            type: "raster",
            source: "osm-tiles",
            minzoom: 0,
            maxzoom: 19,
          },
        ],
      },
      center: [-95.7129, 37.0902],
      zoom: 3.5,
    });

    mapRef.current = map;

    // Wait for map to load before adding data
    map.on("load", () => {
      console.log("Map loaded, adding data...");

      // Skip if no routes
      if (routes.length === 0) return;

      // Process routes
      const allFeatures: Feature[] = [];
      const bounds = new maplibregl.LngLatBounds();

      routes.forEach((route) => {
        if (!route.coords || route.coords.length === 0) return;

        // Create LineString feature
        const lineFeature: Feature = {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: route.coords.map((c) => [c.lng, c.lat]),
          },
          properties: {
            routeId: route.id,
            color: route.color,
          },
        };

        // Extend bounds
        route.coords.forEach((c) => {
          bounds.extend([c.lng, c.lat]);
        });

        allFeatures.push(lineFeature);
      });

      // Skip if no valid features
      if (allFeatures.length === 0) return;

      const featureCollection: FeatureCollection = {
        type: "FeatureCollection",
        features: allFeatures,
      };

      // Add route source & layer
      map.addSource("routes", {
        type: "geojson",
        data: featureCollection,
      });

      map.addLayer({
        id: "routes-layer",
        type: "line",
        source: "routes",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": ["get", "color"],
          "line-width": 3,
        },
      });

      // Fit map to bounds
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds as LngLatBoundsLike, {
          padding: 50,
          animate: true,
        });
      }

      // Add events if available
      if (events && events.length > 0) {
        const eventFeatures: Feature[] = events.map((ev) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [ev.coords.lng, ev.coords.lat],
          },
          properties: {
            eventType: ev.eventType,
            distance: ev.distance,
            date: ev.date,
          },
        }));

        const eventFeatureCollection: FeatureCollection = {
          type: "FeatureCollection",
          features: eventFeatures,
        };

        // Add event source and layer
        map.addSource("events", {
          type: "geojson",
          data: eventFeatureCollection,
        });

        map.addLayer({
          id: "events-layer",
          type: "circle",
          source: "events",
          paint: {
            "circle-radius": 6,
            "circle-color": "#FF0000",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#FFFFFF",
          },
        });

        // Create and configure popup
        const popup = new Popup({
          closeButton: false,
          closeOnClick: false,
        });

        // Show popup on hover
        map.on("mouseenter", "events-layer", (e) => {
          map.getCanvas().style.cursor = "pointer";
          if (!e.features || !e.features[0]) return;

          const feature = e.features[0];
          const { eventType, distance, date } = feature.properties || {};
          const coordinates = (feature.geometry as any).coordinates.slice();

          const popupContent = `
            <div style="font-size:14px">
              <strong>Event Type:</strong> ${eventType}<br/>
              <strong>Distance:</strong> ${distance}<br/>
              <strong>Date:</strong> ${date}
            </div>
          `;

          popup.setLngLat(coordinates).setHTML(popupContent).addTo(map);
        });

        map.on("mouseleave", "events-layer", () => {
          map.getCanvas().style.cursor = "";
          popup.remove();
        });

        // Handle click events
        if (onEventClick) {
          map.on("click", "events-layer", (e) => {
            if (!e.features || !e.features[0]) return;

            const feature = e.features[0];
            const eventType = feature.properties?.eventType;
            const date = feature.properties?.date;

            if (eventType && date) {
              const groupKey = `${eventType}-${date}`;
              onEventClick(groupKey);
            }
          });
        }
      }
    });

    // Handle resize after a slight delay
    setTimeout(() => {
      if (map) map.resize();
    }, 100);
  };

  // Initialize map on first render and when routes change
  useEffect(() => {
    console.log("Routes changed, reinitializing map...");
    initializeMap();

    // Force resize after a delay
    const resizeTimer = setTimeout(() => {
      if (mapRef.current) {
        console.log("Forcing map resize...");
        mapRef.current.resize();
      }
    }, 500);

    // Cleanup on unmount
    return () => {
      clearTimeout(resizeTimer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [routes, events, onEventClick, mapKey]);

  // Add a method that parent components can call to force reinitialization
  // This can be exposed through a ref if needed
  const forceMapRefresh = () => {
    setMapKey(Date.now());
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      ref={mapContainerRef}
      className="w-full h-full rounded shadow-md"
      style={{ minHeight: "400px" }}
    />
  );
};

export default RouteMap;